import React from 'react';
import {
    Box,
    Typography,
    Grid,
    Link,
    IconButton,
    Divider,
    Fade
} from '@mui/material';
import {
    Email as EmailIcon,
    Phone as PhoneIcon,
    Language as WebsiteIcon,
    LocationOn as LocationIcon,
    Copyright as CopyrightIcon,
    BusinessCenter as BusinessIcon,
    VerifiedUser as QualityIcon,
    Engineering as EngineeringIcon,
    Security as VerifiedUser
} from '@mui/icons-material';

// Import Tempco UI components
import {
    TempcoHeading,
    TempcoBodyText,
    TempcoGradientText,
    TempcoStatusChip,
    tempcoColors
} from './TempcoUI';

const Footer = () => {
    const currentYear = new Date().getFullYear();

    return (
        <Fade in={true} timeout={1000}>
            <Box
                component="footer"
                className="tempco-footer"
                sx={{
                    background: '#000000',
                    color: tempcoColors.white,
                    paddingY: 4,
                    paddingX: 4,
                    width: '100%',
                    position: 'relative',
                    minHeight: 'auto',
                    zIndex: 1000,
                }}
            >
                <Box maxWidth="xl" sx={{margin: '0 auto', width: '100%'}}>
                    <Grid container spacing={4}>
                        {/* Order Tracker Web - Left Column */}
                        <Grid item xs={12} md={4}>
                            <TempcoHeading
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    marginBottom: 2,
                                    color: tempcoColors.primary
                                }}
                            >
                                Order Tracker Web
                            </TempcoHeading>

                            {/* Premium Features */}
                            <Box sx={{
                                display: 'grid', 
                                gridTemplateColumns: 'auto auto', 
                                gap: 2,
                                borderRadius: 2,
                                alignItems: 'start'
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <BusinessIcon sx={{ color: tempcoColors.primary, fontSize: 16 }} />
                                    <TempcoBodyText variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        Real-time order tracking
                                    </TempcoBodyText>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <QualityIcon sx={{ color: tempcoColors.primary, fontSize: 16 }} />
                                    <TempcoBodyText variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        Quote management
                                    </TempcoBodyText>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <EngineeringIcon sx={{ color: tempcoColors.primary, fontSize: 16 }} />
                                    <TempcoBodyText variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        Custom manufacturing solutions
                                    </TempcoBodyText>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <VerifiedUser sx={{ color: tempcoColors.primary, fontSize: 16 }} />
                                    <TempcoBodyText variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        First-class secure access
                                    </TempcoBodyText>
                                </Box>
                            </Box>
                        </Grid>

                        {/* Tempco Logo - Center Column */}
                        <Grid item xs={12} md={4}>
                            <Box sx={{
                                display: 'flex', 
                                flexDirection: 'column',
                                alignItems: 'center', 
                                marginBottom: 2,
                                textAlign: 'center'
                            }}>
                                <img
                                    src={process.env.PUBLIC_URL + '/Tempco.png'}
                                    alt='Tempco Electric Heater Corporation'
                                    height='45px'
                                    style={{
                                        marginBottom: 12,
                                        filter: 'brightness(1.1) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
                                    }}
                                />
                                <TempcoGradientText
                                    variant="h6"
                                    component="h3"
                                    sx={{
                                        fontWeight: 600,
                                        lineHeight: 1.3,
                                    }}
                                >
                                    Tempco Electric Heater Corporation
                                </TempcoGradientText>
                            </Box>
                        </Grid>

                        {/* Contact Information - Right Column */}
                        <Grid item xs={12} md={4}>
                            <TempcoHeading
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    marginBottom: 2,
                                    color: tempcoColors.primary
                                }}
                            >
                                Contact Information
                            </TempcoHeading>
                            <Box sx={{
                                display: 'grid', 
                                gridTemplateColumns: 'auto auto', 
                                gap: 2,
                                alignItems: 'start'
                            }}>
                                <Box sx={{
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1.5,
                                    borderRadius: 2,
                                    transition: 'all 0.2s ease',
                                }}>
                                    <LocationIcon sx={{color: tempcoColors.primary, fontSize: 20}}/>
                                    <TempcoBodyText variant="body2">
                                        Wood Dale, Illinois, USA
                                    </TempcoBodyText>
                                </Box>
                                
                                <Box sx={{
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1.5,
                                    borderRadius: 2,
                                    transition: 'all 0.2s ease',
                                }}>
                                    <PhoneIcon sx={{color: tempcoColors.primary, fontSize: 20}}/>
                                    <Link
                                        href="tel:+1-630-350-2252"
                                        sx={{
                                            color: tempcoColors.lightGray,
                                            textDecoration: 'none',
                                            fontFamily: '"Lato", "Arial", "Helvetica", sans-serif',
                                            '&:hover': {
                                                color: tempcoColors.light,
                                                textDecoration: 'underline'
                                            }
                                        }}
                                    >
                                        +1 (630) 350-2252
                                    </Link>
                                </Box>
                                
                                <Box sx={{
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1.5,
                                    borderRadius: 2,
                                    transition: 'all 0.2s ease',
                                }}>
                                    <EmailIcon sx={{color: tempcoColors.primary, fontSize: 20}}/>
                                    <Link
                                        href="mailto:info@tempco.com"
                                        sx={{
                                            color: tempcoColors.lightGray,
                                            textDecoration: 'none',
                                            fontFamily: '"Lato", "Arial", "Helvetica", sans-serif',
                                            '&:hover': {
                                                color: tempcoColors.light,
                                                textDecoration: 'underline'
                                            }
                                        }}
                                    >
                                        info@tempco.com
                                    </Link>
                                </Box>
                                
                                <Box sx={{
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1.5,
                                    borderRadius: 2,
                                    transition: 'all 0.2s ease',
                                }}>
                                    <WebsiteIcon sx={{color: tempcoColors.primary, fontSize: 20}}/>
                                    <Link
                                        href="https://www.tempco.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            color: tempcoColors.lightGray,
                                            textDecoration: 'none',
                                            fontFamily: '"Lato", "Arial", "Helvetica", sans-serif',
                                            '&:hover': {
                                                color: tempcoColors.light,
                                                textDecoration: 'underline'
                                            }
                                        }}
                                    >
                                        www.tempco.com
                                    </Link>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Enhanced Divider with Tempco Branding */}
                    <Box
                        sx={{
                            marginY: 3,
                            height: 3,
                            background: `linear-gradient(90deg, transparent 0%, ${tempcoColors.primary} 20%, ${tempcoColors.light} 50%, ${tempcoColors.primary} 80%, transparent 100%)`,
                            borderRadius: 2,
                            opacity: 0.6,
                        }}
                    />

                    {/* Enhanced Copyright with Tempco Messaging */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 2,
                            padding: 2,
                            borderRadius: 2,
                        }}
                    >
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            <CopyrightIcon sx={{color: tempcoColors.primary, fontSize: 18}}/>
                            <TempcoBodyText
                                variant="body2"
                                sx={{ opacity: 0.8 }}
                            >
                                {currentYear} Tempco Electric Heater Corporation. All rights reserved.
                            </TempcoBodyText>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Fade>
    );
};

export {Footer};